import { Box, Typography } from "@outschool/backpack";
import { CreateOrLoginError } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { changePasswordPath, queryStringToObject } from "@outschool/routes";
import { isValidEmailOrUsername } from "@outschool/text";
import { useAnalytics } from "@outschool/ui-analytics";
import {
  AccountLockedError,
  AccountLockedReason,
  ContinueWithApple,
  ContinueWithFacebook,
  ContinueWithGoogle,
  ContinueWithKakao,
  ContinueWithLine,
  LoginUserFeedback,
  TURNSTILE_ERROR_MESSAGE,
  TurnstileWidget,
  useAuthError,
  useCanShowGoogleAuth,
  useSelfElectedStateFundingProgram,
  useShouldShowKakao,
  useShouldShowLine,
  useTurnstile,
} from "@outschool/ui-auth";
import {
  AccountDisclaimer,
  FieldErrorMessage,
  TrackedButton,
} from "@outschool/ui-components-shared";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import {
  Form,
  FormField,
  Input,
  LoadingIcon,
} from "@outschool/ui-legacy-component-library";
import { required, useFormState } from "@outschool/ui-utils";
import React from "react";

import {
  APPLE_SIGNIN_CLIENT_ID,
  FACEBOOK_APP_ID,
  GOOGLE_CLIENT_ID,
  TURNSTILE_SITEKEY,
} from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import ActionType from "../actions/ActionType";
import {
  useLoginWithApple,
  useLoginWithRedirect,
} from "../hooks/Authentication";
import { useAppState } from "../stores/AppStateProvider";
import Link from "./Link";
import { useMembershipRequirement } from "./subscriptions/hooks/useMembershipRequirement";

// Exported here for easier test setup
export const ContinueWithGoogleWrapped = ContinueWithGoogle;

function getLockInfo(topNotice: string | React.ReactNode): {
  locked: boolean;
  reason?: AccountLockedReason;
} {
  if (typeof topNotice !== "string" || !topNotice.includes("locked")) {
    return { locked: false };
  }
  let reason = null;
  if ((topNotice as string).includes("inactivity")) {
    reason = AccountLockedReason.Inactivity;
  } else if ((topNotice as string).includes("suspicious")) {
    reason = AccountLockedReason.SuspiciousActivity;
  } else {
    reason = AccountLockedReason.TooManyFailedLoginAttempts;
  }
  return { locked: true, reason };
}

export default function LoginForm({
  onSubmit = () => {},
  onRedirect = () => {},
}) {
  const analytics = useAnalytics();
  const { t } = useTranslation("client\\components\\LoginForm");
  const { topNotice } = useTopNoticeContext();
  const canShowGoogleAuth = useCanShowGoogleAuth();

  const appState = useAppState();
  const { fields, validateAll, errors, setters } = useFormState({
    email: {
      value: "",
      validators: [
        value =>
          isValidEmailOrUsername(value ?? "")
            ? null
            : t`Please provide a valid email address`,
      ],
    },
    password: {
      value: "",
      validators: [required(() => t`Please enter your password.`)],
    },
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [userFeedbackMessage, setUserFeedbackMessage] =
    React.useState<React.ReactNode>(null);
  const [hasError, setHasError] = React.useState(false);

  const queries = queryStringToObject(window.location.search);
  const postLoginPath = queries?.postLoginPath;
  const { requiresMembershipForEnrollment } = useMembershipRequirement();
  const { createAccountVariables } = useSelfElectedStateFundingProgram();

  const createParams = React.useMemo(
    () => ({
      requiresMembershipForEnrollment,
      selfElectedStateFundingProgram: createAccountVariables(),
    }),
    [requiresMembershipForEnrollment, createAccountVariables],
  );

  const handleAction = (fn = (_e: unknown) => {}) => {
    return !isLoading ? fn : () => {};
  };

  const resetForm = React.useCallback(() => {
    setHasError(false);
    setUserFeedbackMessage(null);
    onSubmit();
  }, [onSubmit]);

  const {
    turnstileToken,
    error: turnstileError,
    reset: resetTurnstile,
  } = useTurnstile(TURNSTILE_SITEKEY, "turnstile-widget-login-form");

  React.useEffect(() => {
    if ((isLoading && !!topNotice) || turnstileError) {
      resetForm();
      setIsLoading(false);
      const { locked, reason } = getLockInfo(topNotice);
      if (locked) {
        setHasError(locked);
        // this is ok since we only have one error right now
        setUserFeedbackMessage(<AccountLockedError reason={reason} />);
      } else if (topNotice || turnstileError) {
        resetTurnstile();
        if (
          typeof topNotice === "string" &&
          (topNotice.includes("incorrectly") || topNotice.includes("integrity"))
        ) {
          setters.password("");
        }
        setUserFeedbackMessage(topNotice || turnstileError);
      }
    }
  }, [
    topNotice,
    isLoading,
    setters,
    resetForm,
    turnstileError,
    resetTurnstile,
  ]);

  const handleSubmit = (event: $TSFixMe) => {
    event.preventDefault();
    const { hasErrors } = validateAll();
    if (hasErrors) {
      return;
    }
    setIsLoading(true);

    appState.appDispatcher.dispatch(ActionType.User.LOGIN, {
      email: fields.email.value,
      password: fields.password.value,
      postLoginPath,
      turnstileToken,
    });
  };

  const handleAuthError = React.useCallback(
    (
      error: any,
      message: React.ReactNode = t`Sorry that didn’t work, try again`,
    ) => {
      OsPlatform.captureError(error, {
        tags: { component: Component.UserAccountManagement },
      });
      setHasError(true);
      setUserFeedbackMessage(message);
    },
    [t],
  );

  const generateLoginError = useAuthError();

  const handleLoginSuccess = React.useCallback(
    ({
      sessionToken,
      refreshToken,
      postLoginPath,
      isLearnerTransfer,
    }: $TSFixMe) => {
      if (!isLearnerTransfer) {
        appState.appDispatcher.dispatch(ActionType.User.LOGGED_IN, {
          sessionToken,
          refreshToken,
        });
        appState.redirectAfterLogin(postLoginPath);
      } else {
        appState.appDispatcher.dispatch(
          ActionType.User.REDIRECT_TO_LEARNER_APP,
          {
            transferToken: sessionToken,
            isLearnerLogin: true,
          },
        );
      }
    },
    [appState],
  );

  const handleLoginError = React.useCallback(
    (error: CreateOrLoginError) => {
      const errorObject = new Error(error);
      handleAuthError(errorObject, generateLoginError(error));
    },
    [handleAuthError, generateLoginError],
  );

  const handleAppleLogin = useLoginWithApple(
    handleLoginSuccess,
    handleLoginError,
    createParams,
    postLoginPath,
  );

  const setAuthStrategy = useLoginWithRedirect(
    handleLoginError,
    createParams,
    undefined,
    postLoginPath,
  );

  const failureStyle = {
    background: "#FAE3E3",
    border: "2px solid #F15D5D !important",
    boxShadow: "inset 0px 1px 2px rgba(102, 113, 123, 0.21) !important",
  };

  const showKakao = useShouldShowKakao();
  const showLine = useShouldShowLine();

  return (
    <Box
      sx={{
        display: "grid",
        gap: "1em",
        width: "100%",
      }}
    >
      {canShowGoogleAuth && (
        <ContinueWithGoogleWrapped
          trackingName="login-with-google"
          setAuthStrategy={setAuthStrategy}
          getAnonymousId={analytics.anonymousId}
          clientId={GOOGLE_CLIENT_ID!}
        />
      )}

      {showLine && (
        <ContinueWithLine
          trackingName="login-with-line"
          setAuthStrategy={setAuthStrategy}
          getAnonymousId={analytics.anonymousId}
          isLearnerApp={false}
        />
      )}

      {showKakao && (
        <ContinueWithKakao
          setAuthStrategy={setAuthStrategy}
          getAnonymousId={analytics.anonymousId}
          isLearnerApp={false}
          trackingName="login-with-kakao"
        />
      )}

      <ContinueWithFacebook
        setAuthStrategy={setAuthStrategy}
        trackingName="login-with-facebook"
        getAnonymousId={analytics.anonymousId}
        clientId={FACEBOOK_APP_ID!}
      />

      <ContinueWithApple
        onSuccess={handleAppleLogin}
        onError={handleAuthError}
        trackingName="login-with-apple"
        clientId={APPLE_SIGNIN_CLIENT_ID!}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            borderTop: "1px solid #e3e3e3",
            content: '""',
            margin: "0 auto",
            position: "absolute",
            top: "12px",
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            zIndex: -1,
          }}
        />
        <Typography
          variant="inherit"
          sx={{
            padding: "0 20px",
            backgroundColor: "common.white",
          }}
        >
          {t`or log in with email`}
        </Typography>
      </Box>
      {userFeedbackMessage &&
        userFeedbackMessage !== TURNSTILE_ERROR_MESSAGE && (
          <LoginUserFeedback
            userFeedbackMessage={userFeedbackMessage}
            isError={hasError}
          />
        )}
      <TurnstileWidget
        turnstileSitekey={TURNSTILE_SITEKEY}
        id={"turnstile-widget-login-form"}
        hasError={userFeedbackMessage === TURNSTILE_ERROR_MESSAGE}
      />
      <Form
        sx={{ display: "grid", gap: "medium" }}
        onSubmit={handleAction(handleSubmit)}
        noValidate
      >
        {/*
          Hidden inputs added to prevent Safari's aggressive autofill and autofocus behavior.
          Safari automatically fills and focuses visible login fields when credentials are saved.
          This causes issues like redirecting input from other elements (e.g., a search bar).
          These hidden inputs act as decoys for autofill, keeping visible fields unaffected.
        */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          style={{ display: "none" }}
        />
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          style={{ display: "none" }}
        />

        <FormField label={t`Email`} fieldId={fields.email.id}>
          <Input
            name="email"
            autoComplete="email"
            data-test-id="login-form-email"
            type="email"
            sx={errors?.email?.length ? failureStyle : {}}
            {...fields.email}
          />
          {errors.email && <FieldErrorMessage value={errors.email[0]} />}
        </FormField>
        <FormField label={t`Password`} fieldId={fields.password.id}>
          <Input
            name="password"
            autoComplete="current-password"
            sx={errors?.password?.length ? failureStyle : {}}
            data-test-id="login-form-password"
            type="password"
            {...fields.password}
          />
          {errors.password && <FieldErrorMessage value={errors.password[0]} />}
          <Link
            style={{ marginTop: "4px" }}
            onClick={onRedirect}
            to={changePasswordPath()}
          >
            {t`Forgot your password?`}
          </Link>
        </FormField>

        <TrackedButton
          variant="contained"
          type="submit"
          trackingName="login-with-email"
          disabled={
            isLoading ||
            (Boolean(TURNSTILE_SITEKEY) && !Boolean(turnstileToken))
          }
          startIcon={
            isLoading ||
            (Boolean(TURNSTILE_SITEKEY) &&
              !Boolean(turnstileToken) &&
              !turnstileError) ? (
              <LoadingIcon />
            ) : null
          }
        >
          {t`Log In with Email`}
        </TrackedButton>

        <Box
          sx={{
            justifySelf: "center",
          }}
        >
          <Box
            flex
            sx={(theme: $TSFixMe) => ({
              gap: "0.25em",
              flexDirection: "column",

              [theme.breakpoints.up("sm")]: {
                flexDirection: "row",
              },

              alignItems: "center",
            })}
          >
            <span>{t`Don’t have an Outschool account?`}</span>
            <Link
              onClick={onRedirect}
              to={Routes.signupPath()}
              style={{ justifySelf: "center" }}
            >
              {t`Sign up`}
            </Link>
          </Box>
        </Box>
      </Form>
      <AccountDisclaimer isSignup={false} />
    </Box>
  );
}
